import * as React from 'react';
// @ts-expect-error
import type {ContextRouter} from 'react-router-dom';

import {PageControlBar} from 'shells/control-bar/page-control-bar';
import type {Tab as PageControlBarTab} from 'shells/control-bar/page-control-bar';
import {AccountIcon} from 'shells/icon/svg/account-icon';
import {ContactIcon} from 'shells/icon/svg/contact-icon';
import {LeadIconChevron} from 'shells/icon/svg/lead-icon-chevron';

import type {TagType} from 'nutshell-graphql-types';

import {useGetTags} from '../../core/select/hooks';
import {PageContainer} from '../../layout';

import {TagsSetupSidebar} from './tags-setup-sidebar';
import {TagsTable} from './tags-table';

import styles from './tags-page.module.css';

const COMPANY_TAB_NAME = 'company';
const PERSON_TAB_NAME = 'person';
const LEAD_TAB_NAME = 'lead';

const TAB_GRAPHQL_MAP: {
    [key: string]: TagType;
} = {
    [LEAD_TAB_NAME]: 'LEAD',
    [COMPANY_TAB_NAME]: 'ACCOUNT',
    [PERSON_TAB_NAME]: 'CONTACT',
};

const COMPANY_VIEW_TAB_NAME = 'Company';
const PERSON_VIEW_TAB_NAME = 'Person';
const LEAD_VIEW_TAB_NAME = 'Lead';

type Props = ContextRouter;

export function TagsPage(props: Props) {
    const tab = props.match.params.tab || LEAD_TAB_NAME;

    const searchParams = new URLSearchParams(props.history.location.search);
    const archived = searchParams.get('archived') === 'true';

    const selectedEntity: TagType = TAB_GRAPHQL_MAP[tab] || 'LEAD';

    // Fetch active tags for all entities (for sidebar counts)
    const {
        tags: leadTags,
        isLoading: leadsLoading,
        errorMessage: leadsErrorMessage,
        hasNextPage: hasMoreLeadTags,
        totalCount: leadTotalCount,
        fetchMore: fetchMoreLeadTags,
        refetch: refetchLeadTags,
    } = useGetTags({tagType: 'LEAD'});

    const {
        tags: accountTags,
        isLoading: accountsLoading,
        errorMessage: accountsErrorMessage,
        hasNextPage: hasMoreAccountTags,
        totalCount: accountTotalCount,
        fetchMore: fetchMoreAccountTags,
        refetch: refetchAccountTags,
    } = useGetTags({tagType: 'ACCOUNT'});

    const {
        tags: contactTags,
        isLoading: contactsLoading,
        errorMessage: contactsErrorMessage,
        hasNextPage: hasMoreContactTags,
        totalCount: contactTotalCount,
        fetchMore: fetchMoreContactTags,
        refetch: refetchContactTags,
    } = useGetTags({tagType: 'CONTACT'});

    // Archived tags for the selected entity (only when viewing archived)
    const {
        tags: archivedTags,
        isLoading: archivedLoading,
        errorMessage: archivedError,
        hasNextPage: archivedHasMore,
        totalCount: archivedTotalCount,
        fetchMore: archivedFetchMore,
        refetch: refetchArchivedTags,
    } = useGetTags({tagType: selectedEntity, archived: true});

    const tabs: PageControlBarTab[] = React.useMemo(
        () => [
            {
                icon: AccountIcon,
                label: COMPANY_VIEW_TAB_NAME,
                id: COMPANY_VIEW_TAB_NAME.toLowerCase(),
                href: `/setup/tag/${COMPANY_TAB_NAME}`,
            },
            {
                icon: ContactIcon,
                label: PERSON_VIEW_TAB_NAME,
                id: PERSON_VIEW_TAB_NAME.toLowerCase(),
                href: `/setup/tag/${PERSON_TAB_NAME}`,
            },
            {
                icon: LeadIconChevron,
                label: LEAD_VIEW_TAB_NAME,
                id: LEAD_VIEW_TAB_NAME.toLowerCase(),
                href: `/setup/tag/${LEAD_TAB_NAME}`,
            },
        ],
        []
    );

    const handleRefetch = () => {
        refetchArchivedTags();
        switch (selectedEntity) {
            case 'ACCOUNT':
                refetchAccountTags();
                break;
            case 'CONTACT':
                refetchContactTags();
                break;
            case 'LEAD':
                refetchLeadTags();
                break;
        }
    };

    return (
        <PageContainer
            title='Tags'
            whiteBackground={true}
            noPadding={true}
            controlBar={<PageControlBar tabs={{options: tabs, selectedTab: tab}} />}
        >
            <div className='flex gap-16 overflow-auto pad-16 full-height'>
                <div className={styles['sidebar-section']}>
                    <TagsSetupSidebar
                        activeTable={selectedEntity}
                        accountTagCount={accountTotalCount}
                        accountTagsLoading={accountsLoading}
                        leadTagCount={leadTotalCount}
                        leadTagsLoading={leadsLoading}
                        contactTagCount={contactTotalCount}
                        contactTagsLoading={contactsLoading}
                        archivedTagCount={archivedTotalCount}
                        archivedTagsLoading={archivedLoading}
                    />
                </div>
                <TagsTable
                    tagType={selectedEntity}
                    isViewingArchived={archived}
                    onRefetch={handleRefetch}
                    {...(() => {
                        if (archived) {
                            return {
                                tags: archivedTags || [],
                                isLoading: archivedLoading && !archivedTags,
                                errorMessage: archivedError,
                                hasNextPage: archivedHasMore,
                                tagCount: archivedTotalCount,
                                fetchMore: (searchTerm) =>
                                    archivedFetchMore(searchTerm, selectedEntity),
                            };
                        }

                        switch (selectedEntity) {
                            case 'ACCOUNT':
                                return {
                                    tags: accountTags || [],
                                    isLoading: accountsLoading && !accountTags,
                                    errorMessage: accountsErrorMessage,
                                    hasNextPage: hasMoreAccountTags,
                                    tagCount: accountTotalCount,
                                    fetchMore: fetchMoreAccountTags,
                                };
                            case 'CONTACT':
                                return {
                                    tags: contactTags || [],
                                    isLoading: contactsLoading && !contactTags,
                                    errorMessage: contactsErrorMessage,
                                    hasNextPage: hasMoreContactTags,
                                    tagCount: contactTotalCount,
                                    fetchMore: fetchMoreContactTags,
                                };
                            case 'LEAD':
                            default:
                                return {
                                    tags: leadTags || [],
                                    isLoading: leadsLoading && !leadTags,
                                    errorMessage: leadsErrorMessage,
                                    hasNextPage: hasMoreLeadTags,
                                    tagCount: leadTotalCount,
                                    fetchMore: fetchMoreLeadTags,
                                };
                        }
                    })()}
                />
            </div>
        </PageContainer>
    );
}
